/**
 * Mastodon hashtag normalisation, feed-URI ids, and pure followed-tag cache helpers.
 * Shared between the Home-tab plumbing (adapters, tab prefs) and the hashtag page's
 * follow/unfollow control.
 */

export interface MastodonFollowedTag {
    name: string
}

/** URI prefix for a Mastodon followed-hashtag Home tab (`mastodon-hashtag:{normalizedName}`). */
export const MASTODON_HASHTAG_URI_PREFIX = 'mastodon-hashtag:'

/**
 * Normalise a raw hashtag name for ids, dedupe, and comparison — never for display.
 * Mirrors Mastodon's own `HashtagNormalizer`: NFKC-normalise, trim, strip a single
 * leading `#`, trim again (a server can send `'# Foo'`), then lowercase. Trim MUST
 * precede the `#` strip so `' #Foo '` and `'#Foo '` normalise identically.
 */
export function normalizeMastodonTagName(raw: string): string {
    return raw.normalize('NFKC').trim().replace(/^#/, '').trim().toLowerCase()
}

/** Mastodon's own hashtag charset: letters, numbers, and underscore — no punctuation, no dots. */
const VALID_HASHTAG_NAME = /^[\p{L}\p{N}_]+$/u

/**
 * Whether a *normalised* hashtag name is safe to use in an API path segment (and worth
 * offering a Follow control for). Rejects `.`, `..`, and any other punctuation-only name
 * that would otherwise survive normalisation and — because `encodeURIComponent` never
 * encodes `.` — collapse a `/api/v1/tags/:name/follow` (or `/timelines/tag/:name`) URL to
 * an unintended path once passed through the `URL` constructor. Always call this on the
 * normalised name, never the raw or display string.
 */
export function isValidMastodonTagName(normalized: string): boolean {
    return VALID_HASHTAG_NAME.test(normalized)
}

/** Build the Home-tab id for a followed hashtag. Caller must guard against an empty name. */
export function mastodonHashtagTabId(rawName: string): string {
    return MASTODON_HASHTAG_URI_PREFIX + normalizeMastodonTagName(rawName)
}

/** Extract the normalised tag name from a `mastodon-hashtag:` tab id, or null if not one. */
export function parseMastodonHashtagTabId(uri: string): string | null {
    if (!uri.startsWith(MASTODON_HASHTAG_URI_PREFIX)) return null
    const name = uri.slice(MASTODON_HASHTAG_URI_PREFIX.length)
    return name.length > 0 ? name : null
}

/**
 * Display-only variant of the normaliser: trim, strip a single leading `#`, trim again.
 * Preserves server casing and never applies NFKC/lowercasing. Never feed the result back
 * into ids or comparisons — use `normalizeMastodonTagName` for that.
 */
/** Unicode control (Cc) and format (Cf, incl. bidi overrides like U+202E) characters. */
const BIDI_AND_CONTROL_CHARS = /[\p{Cc}\p{Cf}]/gu

export function mastodonTagDisplayName(name: string): string {
    return '#' + name.trim().replace(/^#/, '').trim().replace(BIDI_AND_CONTROL_CHARS, '')
}

/** Whether `tags` already contains `rawName` (compared by normalised name). */
export function isTagFollowed(tags: MastodonFollowedTag[] | undefined, rawName: string): boolean {
    if (!tags) return false
    const normalized = normalizeMastodonTagName(rawName)
    return tags.some(t => normalizeMastodonTagName(t.name) === normalized)
}

/** Return a new array with `tag` present (unchanged if already present by normalised name). */
export function withTagFollowed(
    old: MastodonFollowedTag[] | undefined,
    tag: MastodonFollowedTag
): MastodonFollowedTag[] {
    const list = old ?? []
    if (isTagFollowed(list, tag.name)) return list
    return [...list, tag]
}

/** Return a new array with any entry matching `rawName` (by normalised name) removed. */
export function withTagUnfollowed(
    old: MastodonFollowedTag[] | undefined,
    rawName: string
): MastodonFollowedTag[] | undefined {
    if (!old) return undefined
    const normalized = normalizeMastodonTagName(rawName)
    return old.filter(t => normalizeMastodonTagName(t.name) !== normalized)
}
